<template>
  <div class="workflow-stats-file">
    <!-- Banner info mode contact -->
    <div v-if="result?.filterMode === 'contact' && result.contactInfo" class="wsf-contact-banner">
      <i class="far fa-user mr-2" />
      <strong>{{ t('workflowStats.contactMode.title') }}</strong>
      <span class="ml-2">
        {{ result.contactInfo.firstName }} {{ result.contactInfo.lastName }}
        <span v-if="result.contactInfo.email" class="wsf-contact-email">
          ({{ result.contactInfo.email }})
        </span>
      </span>
      <button
        type="button"
        class="wsf-clear-filter"
        @click="clearContactFilter"
      >
        <i class="far fa-xmark mr-1" />
        {{ t('workflowStats.contactMode.clear') }}
      </button>
    </div>

    <div class="wsf-body">
      <!-- Sidebar filtres -->
      <aside class="wsf-filters">
        <h4 class="wsf-filters__title">
          <i class="far fa-filter mr-2" />
          {{ t('workflowStats.filters.title') }}
        </h4>
        <!--
          Date range : on réutilise `CustomDatePicker` (composant standard
          du projet, cf. ToolbarButtons / StatsBuilderFilters). UI compacte :
          bouton avec presets (« 30 derniers jours », « 7 derniers jours », …)
          + Calendar additionnel quand « Personnalisé ». Format de date du
          Calendar déjà géré par primevue selon la locale globale.
        -->
        <div class="wsf-filters__field">
          <label>{{ t('workflowStats.dateRange.label') }}</label>
          <CustomDatePicker
            v-model="dateInterval"
            calendar-position="bottom"
            :number-of-months="2"
            :manual-input="true"
            class="wsf-date-picker"
            @value-choosed="onIntervalChange"
          />
        </div>
        <div class="wsf-filters__field">
          <label>{{ t('workflowStats.filters.email') }}</label>
          <input v-model="filters.email" type="email" class="wsf-input">
        </div>
        <div class="wsf-filters__field">
          <label>{{ t('workflowStats.filters.idShopContact') }}</label>
          <input v-model.number="filters.idShopContact" type="number" class="wsf-input">
        </div>
        <div class="wsf-filters__field">
          <label>{{ t('workflowStats.filters.idShopVisitor') }}</label>
          <input v-model.number="filters.idShopVisitor" type="number" class="wsf-input">
        </div>
        <div class="wsf-filters__field">
          <label>{{ t('workflowStats.filters.idStatsSend') }}</label>
          <input v-model.number="filters.idStatsSend" type="number" class="wsf-input">
        </div>
        <div class="wsf-filters__actions">
          <button
            type="button"
            class="wsf-btn wsf-btn--primary"
            :disabled="isLoading"
            @click="applyFilters"
          >
            {{ t('workflowStats.filters.apply') }}
          </button>
          <button
            type="button"
            class="wsf-btn wsf-btn--ghost"
            :disabled="isLoading"
            @click="resetFilters"
          >
            {{ t('workflowStats.filters.reset') }}
          </button>
        </div>
      </aside>

      <!-- Main area : header + canvas FlowRenderer -->
      <main class="wsf-main">
        <div v-if="isLoading" class="wsf-state">
          <i class="far fa-spinner fa-spin mr-2" />
          {{ t('workflowStats.loading') }}
        </div>
        <div v-else-if="error" class="wsf-state wsf-state--error">
          <i class="far fa-circle-exclamation mr-2" />
          {{ error }}
        </div>
        <template v-else-if="result">
          <div class="wsf-tree__head">
            <h3 class="wsf-tree__title">
              {{ result.workflow.name }}
            </h3>
            <p class="wsf-tree__meta">
              {{ t('workflowStats.tree.stepCount', { count: result.workflow.flow.steps.length }) }}
              ·
              {{ t(`workflowStats.tree.type.${result.workflow.type}`) }}
              <span v-if="result.filterMode === 'contact'" class="wsf-tree__meta-pill">
                <i class="far fa-route mr-1" />
                {{ t('workflowStats.tree.contactPath') }}
              </span>
            </p>
            <!--
              Sélecteur de parcours : ne s'affiche qu'en mode contact ET
              quand le contact a déclenché le scénario plus d'une fois sur
              la période. Les options sont numérotées chronologiquement
              (#1 = plus ancien). Au changement on refetch avec
              idWorkflowInstance ciblé.
            -->
            <div
              v-if="result.filterMode === 'contact' && hasMultipleInstances"
              class="wsf-tree__instances"
            >
              <label class="wsf-tree__instances-label">
                <i class="far fa-route" />
                {{ t('workflowStats.tree.instanceSelector.label') }}
              </label>
              <select
                v-model.number="selectedInstanceId"
                class="wsf-tree__instances-select"
                :disabled="isLoading"
                @change="onInstanceChange"
              >
                <option
                  v-for="(inst, idx) in result.availableInstances"
                  :key="inst.idWorkflow"
                  :value="inst.idWorkflow"
                >
                  {{ t('workflowStats.tree.instanceSelector.optionLabel', {
                    n: idx + 1,
                    date: formatInstanceDate(inst.firstEnteredAt),
                  }) }}
                </option>
              </select>
            </div>
          </div>
          <!--
            Mode contact : si aucun step du contact ne matche le scénario
            sur la période, on affiche un état vide explicite plutôt que
            l'arbre grisé (qui laissait l'utilisateur sans repère sur la
            cause).
          -->
          <div
            v-if="result.filterMode === 'contact' && analyticsStepCount === 0"
            class="wsf-state wsf-state--empty"
          >
            <i class="far fa-circle-question wsf-state__icon" />
            <h4 class="wsf-state__title">
              {{ t('workflowStats.contactMode.empty.title') }}
            </h4>
            <p class="wsf-state__desc">
              {{ t('workflowStats.contactMode.empty.description') }}
            </p>
          </div>
          <div
            v-else
            class="wsf-canvas"
            :class="{ 'wsf-canvas--contact-mode': result.filterMode === 'contact' }"
          >
            <FlowRenderer
              :flow="result.workflow.flow"
              :type="rendererType"
              :empty-label="t('workflowStats.tree.empty')"
            />
          </div>
        </template>
      </main>
    </div>
  </div>
</template>

<script lang="ts">
import {
  defineComponent, computed, ref, onMounted, onUnmounted, watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import moment from 'moment';
import { useStore } from '@/store';
import FlowRenderer from '@/components/flows/FlowRenderer.vue';
import CustomDatePicker from '@/components/fields/CustomDatePicker.vue';
import { useGlobalFlowAnalytics } from '@/composables/flows/useFlowAnalytics';
import {
  fetchWorkflowStatsTree,
  WorkflowStatsTreeResult,
  WorkflowStatsTreeFilter,
} from '@/composables/flows/useFlowApi';
import { IntervalDateEnum, TypeCampaignEnum } from '@/types';

// Convertit l'objet `{ interval, customDateRange }` de CustomDatePicker en
// bornes ISO YYYY-MM-DD attendues par l'API stats-tree. Aligné sur la
// logique de `getAnalyticsDateRange` (useFlowApi.ts) mais avec format
// YYYY-MM-DD au lieu de YYYYMMDDHH.
const intervalToIsoRange = (
  interval: IntervalDateEnum | 'empty' | string,
  customDateRange?: Date[] | null,
): { dateStart: string; dateEnd: string } => {
  const today = moment();
  const fmt = 'YYYY-MM-DD';
  switch (interval) {
    case IntervalDateEnum.LAST_DAY:
      return { dateStart: today.clone().subtract(1, 'days').format(fmt), dateEnd: today.format(fmt) };
    case IntervalDateEnum.LAST_3_DAYS:
      return { dateStart: today.clone().subtract(3, 'days').format(fmt), dateEnd: today.format(fmt) };
    case IntervalDateEnum.LAST_7_DAYS:
      return { dateStart: today.clone().subtract(7, 'days').format(fmt), dateEnd: today.format(fmt) };
    case IntervalDateEnum.LAST_14_DAYS:
      return { dateStart: today.clone().subtract(14, 'days').format(fmt), dateEnd: today.format(fmt) };
    case IntervalDateEnum.LAST_30_DAYS:
      return { dateStart: today.clone().subtract(30, 'days').format(fmt), dateEnd: today.format(fmt) };
    case IntervalDateEnum.LAST_3_MONTHS:
      return { dateStart: today.clone().subtract(3, 'months').format(fmt), dateEnd: today.format(fmt) };
    case IntervalDateEnum.LAST_12_MONTHS:
      return { dateStart: today.clone().subtract(12, 'months').format(fmt), dateEnd: today.format(fmt) };
    case IntervalDateEnum.CUSTOM_DATE_RANGE:
      if (customDateRange && customDateRange.length === 2 && customDateRange[0] && customDateRange[1]) {
        return {
          dateStart: moment(customDateRange[0]).format(fmt),
          dateEnd: moment(customDateRange[1]).format(fmt),
        };
      }
      return { dateStart: today.clone().subtract(30, 'days').format(fmt), dateEnd: today.format(fmt) };
    default:
      // 'empty' ou inconnu → 30 derniers jours par défaut.
      return { dateStart: today.clone().subtract(30, 'days').format(fmt), dateEnd: today.format(fmt) };
  }
};

const formatInstanceDate = (iso: string, locale: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(locale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export default defineComponent({
  name: 'WorkflowStatsFile',
  components: { FlowRenderer, CustomDatePicker },

  setup() {
    const { t, locale } = useI18n();
    const store = useStore();
    const flowAnalytics = useGlobalFlowAnalytics();

    const selectedData = computed(() => store.getters['dataExplorer/getSelectedData']);

    // Période initiale : on s'aligne sur le filtre date du rapport
    // statistique courant (`statsEditor/getPageFilters.date_interval`) si
    // disponible — l'utilisateur arrive ici via le kebab d'un tableau
    // stats et s'attend à voir la même fenêtre. Fallback 30 derniers
    // jours si le store n'a pas de date_interval (ouverture hors contexte
    // stats ou store non initialisé).
    const initialDateInterval = ((): {
      interval: IntervalDateEnum | 'empty' | string;
      customDateRange?: Date[];
    } => {
      const pageFilters = store.getters['statsEditor/getPageFilters'];
      const fromStore = pageFilters?.date_interval?.value;
      if (fromStore?.interval) {
        return {
          interval: fromStore.interval,
          ...(fromStore.customDateRange ? { customDateRange: fromStore.customDateRange } : {}),
        };
      }
      return { interval: IntervalDateEnum.LAST_30_DAYS };
    })();
    const dateInterval = ref<{ interval: IntervalDateEnum | 'empty' | string; customDateRange?: Date[] }>(
      initialDateInterval,
    );
    const initialRange = intervalToIsoRange(
      initialDateInterval.interval,
      initialDateInterval.customDateRange,
    );
    const dateStart = ref<string>(initialRange.dateStart);
    const dateEnd = ref<string>(initialRange.dateEnd);

    const filters = ref<WorkflowStatsTreeFilter>({
      ...((selectedData.value?.initialFilter as WorkflowStatsTreeFilter | null) ?? {}),
    });

    const isLoading = ref(false);
    const error = ref<string | null>(null);
    const result = ref<WorkflowStatsTreeResult | null>(null);

    // Sélecteur de parcours (mode contact multi-instances). Initialement
    // synchronisé sur `result.selectedInstanceId` (= la plus récente par
    // défaut côté backend), puis pilote le refetch sur changement.
    const selectedInstanceId = ref<number | null>(null);
    const hasMultipleInstances = computed(
      () => (result.value?.availableInstances?.length ?? 0) > 1,
    );

    const idWorkflow = computed(() => Number(selectedData.value?.idWorkflow ?? 0));
    const type = computed<'automation' | 'bulk'>(
      () => (selectedData.value?.type as 'automation' | 'bulk') ?? 'automation',
    );

    // FlowRenderer attend un CampaignType (enum), pas un string literal.
    const rendererType = computed(() => (
      result.value?.workflow.type === 'bulk'
        ? TypeCampaignEnum.BULK
        : TypeCampaignEnum.AUTOMATION
    ));

    const load = async (overrideInstanceId?: number | null) => {
      if (!idWorkflow.value) return;
      isLoading.value = true;
      error.value = null;
      try {
        // Construit le filtre en injectant idWorkflowInstance quand on a
        // une sélection explicite (changement manuel du dropdown). Quand
        // overrideInstanceId est null (= reset), on retire le paramètre
        // pour laisser le backend re-déterminer le défaut.
        const effectiveFilter: WorkflowStatsTreeFilter = { ...filters.value };
        if (overrideInstanceId !== undefined && overrideInstanceId !== null) {
          effectiveFilter.idWorkflowInstance = overrideInstanceId;
        }
        result.value = await fetchWorkflowStatsTree(
          type.value,
          idWorkflow.value,
          dateStart.value,
          dateEnd.value,
          effectiveFilter,
        );
      } catch (err: any) {
        error.value = err?.message ?? t('workflowStats.errorGeneric');
        result.value = null;
      } finally {
        isLoading.value = false;
      }
    };

    // Quand le backend renvoie un selectedInstanceId, on synchronise la
    // ref locale pour aligner le dropdown sur la sélection effective.
    watch(
      () => result.value?.selectedInstanceId,
      (val) => {
        if (val) selectedInstanceId.value = val;
        else selectedInstanceId.value = null;
      },
    );

    const onInstanceChange = () => {
      if (selectedInstanceId.value) load(selectedInstanceId.value);
    };

    // Watch sur l'intervalle pour resync dateStart/dateEnd + refetch.
    // CustomDatePicker emit `update:modelValue` à chaque change (preset
    // cliqué OU custom range complété). Le watch initial-callback est
    // skip via { immediate: false } — load() sera appelé une fois au
    // mount (onMounted ci-dessous).
    watch(
      dateInterval,
      (val) => {
        const { dateStart: ds, dateEnd: de } = intervalToIsoRange(
          val?.interval ?? 'empty',
          // primevue tape les dates en Date-like (sans Symbol.toPrimitive)
          // dans le v-model du Calendar — cast nécessaire pour l'aligner
          // sur la signature `Date[]` du helper.
          val?.customDateRange as Date[] | undefined,
        );
        // Skip si rien n'a changé (évite refetch redondant lorsque le
        // composant re-emit le même payload).
        if (ds === dateStart.value && de === dateEnd.value) return;
        dateStart.value = ds;
        dateEnd.value = de;
        selectedInstanceId.value = null;
        load();
      },
      { deep: true },
    );

    const onIntervalChange = () => {
      // Hook prévu par CustomDatePicker mais on garde la logique dans le
      // watch ci-dessus pour couvrir aussi l'init.
    };

    // Compteur pratique pour le template (état vide quand contact mode +
    // 0 step matché).
    const analyticsStepCount = computed(
      () => Object.keys(result.value?.analyticsByStep ?? {}).length,
    );

    const formatInstanceDateLocal = (iso: string) => formatInstanceDate(iso, locale.value);

    // Synchronise le singleton analytics avec les données reçues.
    // FlowRenderer/FlowNode lisent ces données via useGlobalFlowAnalytics().
    watch(result, (val) => {
      if (val) {
        const analyticsKeys = Object.keys(val.analyticsByStep ?? {});
        const flow: any = val.workflow?.flow ?? {};
        const steps: any[] = Array.isArray(flow.steps) ? flow.steps : [];
        // eslint-disable-next-line no-console
        console.log('[WSTREE-DEBUG] front received', {
          filterMode: val.filterMode,
          contactInfo: val.contactInfo,
          analyticsKeysCount: analyticsKeys.length,
          analyticsKeys,
          flowStepsCount: steps.length,
          rootStepId: flow.rootStepId,
          flowSteps: steps.map((s: any) => ({
            id: s?.id,
            type: s?.type,
            class: s?.class,
            primaryParentId: s?.primaryParent?.id,
            childrenIds: Array.isArray(s?.children)
              ? s.children.map((c: any) => c?.id)
              : [],
            hasMatchInAnalytics: !!val.analyticsByStep?.[s?.id],
          })),
          rawFlowKeys: Object.keys(flow),
        });
        flowAnalytics.enableAnalyticsMode();
        flowAnalytics.setAnalyticsData(val.analyticsByStep ?? {});
      }
    });

    const reload = () => load();
    const applyFilters = () => {
      // Nouveau filtre user = re-déterminer le défaut côté backend.
      selectedInstanceId.value = null;
      load();
    };
    const resetFilters = () => {
      filters.value = {};
      selectedInstanceId.value = null;
      load();
    };
    const clearContactFilter = () => {
      filters.value = {};
      selectedInstanceId.value = null;
      load();
    };

    onMounted(() => {
      load();
    });

    onUnmounted(() => {
      // Réinitialise le singleton pour ne pas polluer l'éditeur de scénario.
      flowAnalytics.setAnalyticsData({});
      flowAnalytics.disableAnalyticsMode();
    });

    return {
      t,
      dateStart,
      dateEnd,
      dateInterval,
      onIntervalChange,
      analyticsStepCount,
      filters,
      isLoading,
      error,
      result,
      rendererType,
      selectedInstanceId,
      hasMultipleInstances,
      reload,
      applyFilters,
      resetFilters,
      clearContactFilter,
      onInstanceChange,
      formatInstanceDate: formatInstanceDateLocal,
    };
  },
});
</script>

<style lang="scss" scoped>
.workflow-stats-file {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #f8f9fa;
}

.wsf-date-picker {
  width: 100%;

  // CustomDatePicker rend un trigger en flex — on l'aligne sur le
  // style des autres inputs filtres (largeur full, border, padding).
  :deep(.custom_datepicker_input) {
    width: 100%;
    padding: 0.5rem 0.75rem;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    background: #fff;
    font-size: 0.85rem;
    cursor: pointer;
  }
}

.wsf-contact-banner {
  display: flex;
  align-items: center;
  padding: 0.65rem 1.25rem;
  background: #eff6ff;
  border-bottom: 1px solid #bfdbfe;
  font-size: 0.85rem;
  color: #1e3a8a;
  flex: 0 0 auto;
}

.wsf-contact-email {
  color: #6b7280;
}

.wsf-clear-filter {
  margin-left: auto;
  padding: 0.25rem 0.6rem;
  border: 1px solid #bfdbfe;
  border-radius: 4px;
  background: #fff;
  cursor: pointer;
  font-size: 0.78rem;
  color: #1e3a8a;
}

.wsf-body {
  display: grid;
  // Sidebar légèrement plus large pour accueillir le trigger
  // CustomDatePicker (label + chevron) sans wrap.
  grid-template-columns: 265px 1fr;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.wsf-filters {
  background: #fff;
  border-right: 1px solid #e5e7eb;
  padding: 1rem;
  overflow-y: auto;
}

.wsf-filters__title {
  font-size: 0.85rem;
  font-weight: 700;
  color: #1f2937;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin: 0 0 1rem 0;
}

.wsf-filters__field {
  margin-bottom: 0.75rem;

  label {
    display: block;
    font-size: 0.75rem;
    color: #6b7280;
    margin-bottom: 0.25rem;
  }
}

.wsf-input {
  width: 100%;
  padding: 0.4rem 0.6rem;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 0.85rem;
}

.wsf-filters__actions {
  display: flex;
  gap: 0.5rem;
  margin-top: 1rem;
}

.wsf-btn {
  flex: 1;
  padding: 0.45rem 0.75rem;
  border-radius: 4px;
  font-size: 0.82rem;
  cursor: pointer;
  border: 1px solid transparent;

  &--primary {
    background: var(--brand-color-primary, #84cc16);
    color: #fff;
    border-color: var(--brand-color-primary, #84cc16);

    &:hover:not(:disabled) {
      filter: brightness(0.95);
    }
  }
  &--ghost {
    background: #fff;
    color: #6b7280;
    border-color: #d1d5db;

    &:hover:not(:disabled) {
      background: #f3f4f6;
    }
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.wsf-main {
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}

.wsf-state {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 3rem 1rem;
  color: #6b7280;

  &--error {
    color: #b91c1c;
  }

  // État vide explicite (mode contact, 0 step matché). Centré, icône
  // large, copy à 2 niveaux pour expliquer ce qui se passe et comment
  // débloquer la situation côté utilisateur.
  &--empty {
    flex-direction: column;
    gap: 0.5rem;
    padding: 4rem 1.5rem;
    text-align: center;
  }

  &__icon {
    font-size: 2.5rem;
    color: #cbd5e1;
    margin-bottom: 0.5rem;
  }

  &__title {
    margin: 0;
    font-size: 1rem;
    font-weight: 600;
    color: #374151;
  }

  &__desc {
    margin: 0;
    max-width: 28rem;
    font-size: 0.85rem;
    color: #6b7280;
    line-height: 1.45;
  }
}

.wsf-tree__head {
  padding: 0.85rem 1.25rem;
  border-bottom: 1px solid #e5e7eb;
  background: #fff;
  flex: 0 0 auto;
}

.wsf-tree__title {
  margin: 0 0 0.25rem 0;
  font-size: 1.05rem;
  font-weight: 700;
  color: #1f2937;
}

.wsf-tree__meta {
  margin: 0;
  font-size: 0.82rem;
  color: #6b7280;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.wsf-tree__meta-pill {
  display: inline-flex;
  align-items: center;
  padding: 0.15rem 0.5rem;
  border-radius: 999px;
  background: var(--brand-color-primary-soft, #ecfccb);
  color: var(--brand-color-primary-dark, #4d7c0f);
  font-size: 0.72rem;
  font-weight: 600;
}

.wsf-tree__instances {
  margin-top: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.wsf-tree__instances-label {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.78rem;
  font-weight: 600;
  color: #475569;

  i {
    font-size: 0.72rem;
  }
}

.wsf-tree__instances-select {
  padding: 0.3rem 0.55rem;
  border-radius: 6px;
  border: 1px solid #d1d5db;
  background: #fff;
  font-size: 0.78rem;
  color: #1f2937;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.wsf-canvas {
  flex: 1;
  min-height: 0;
  position: relative;
  overflow: hidden;
  background: #f8f9fa;

  // FlowRenderer occupe 100% du conteneur (pan/zoom interne).
  :deep(.flow-renderer),
  :deep(.flow-renderer__canvas-wrapper) {
    width: 100%;
    height: 100%;
  }

  // Mode contact : on dim opacité 0.3 sur les nodes hors parcours (= sans
  // `.flow-analytics-panel` injecté par FlowAnalyticsChip), et on colore
  // les edges entre 2 nodes traversés (classe `--traversed` posée par
  // FlowRenderer via isStepTraversed()).
  &--contact-mode {
    :deep(.flow-node:not(:has(.flow-analytics-panel))) {
      opacity: 0.3;
      transition: opacity 0.2s ease;
    }

    :deep(.flow-renderer__connections path) {
      opacity: 0.18;
      transition: opacity 0.2s ease, stroke 0.2s ease, stroke-width 0.2s ease;
    }

    :deep(.flow-renderer__connection-branch--traversed) {
      opacity: 1;
      stroke: var(--brand-color-primary, #84cc16);
      stroke-width: 2.5;
    }
  }
}
</style>

